import type { OrderDetails } from '@/entities/order/model'
import { Flex, Loader, Text, Title } from '@mantine/core'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { fetchOrders } from '@/entities/order/api'
import { orderKeys, useRawOrdersQuery } from '@/entities/order/model'
import { fetchStateConfig } from '@/entities/state/api'
import { stateKeys } from '@/entities/state/model'
import { StatusOrderCard } from '@/features/order/ui/status-order-card'
import { filterOrdersBySearch } from '@/features/search/lib'
import { useSearchStore } from '@/features/search/model'

export const Route = createFileRoute('/_dashboard/dashboard/overdue')({
  component: OverdueComponent,
  beforeLoad: async ({ context: { queryClient } }) => {
    const stateConfig = await queryClient.ensureQueryData({
      queryKey: stateKeys.stateConfig(),
      queryFn: fetchStateConfig,
    })

    // fallback when overdue is disabled on hamburger list
    if (stateConfig?.features?.overdueHomeEnabled) {
      throw redirect({
        to: '/dashboard',
        replace: true,
      })
    }
  },
  loader: ({ context: { queryClient } }) => {
    queryClient.prefetchQuery({
      queryKey: orderKeys.orders(),
      queryFn: fetchOrders,
    })
  },
  pendingComponent: () => (
    <Flex direction="column" align="center" justify="center" style={{ flex: 1, minHeight: 300 }}>
      <Loader size="xl" />
      <Text mt="md">Loading orders...</Text>
    </Flex>
  ),
  errorComponent: error => (
    <Flex direction="column" align="center" justify="center">
      <Text c="red">
        Error:
        {' '}
        {error instanceof Error ? error.message : 'An unexpected error occurred while loading orders.'}
      </Text>
    </Flex>
  ),
})

function OverdueComponent() {
  const { data: orders } = useRawOrdersQuery()
  const { searchQuery, clearSearch } = useSearchStore()

  useEffect(() => {
    clearSearch()
  }, [clearSearch])

  const overdueOrders = useMemo(() => {
    // Filter for only overdue orders and flatten into single orders
    const overdueOnly = orders
      .filter(order => order.status === 'OrderOverdue')
      .map(order => ({
        type: 'single' as const,
        id: order.orderId,
        data: order,
      }))

    // Apply search filtering if there's a search query
    return searchQuery ? filterOrdersBySearch(overdueOnly, searchQuery) : overdueOnly
  }, [orders, searchQuery])

  return (
    <Flex direction="column" p="md">
      <Title order={2} mb="md">Overdue orders</Title>
      {overdueOrders.length === 0 ? (
        <Text ta="center" c="dimmed">No overdue orders</Text>
      ) : (
        <Flex direction="column" gap="sm">
          {overdueOrders.map(dashboardOrder => (
            <StatusOrderCard
              key={dashboardOrder.id}
              order={dashboardOrder.data as OrderDetails}
            />
          ))}
        </Flex>
      )}
    </Flex>
  )
}
